/**
 * GAME SCENE - Escena Principal del Juego
 * Implementa toda la mecánica de Mala Hierba
 */

import Phaser from "phaser";
import {
  SCENES,
  COMPONENTS,
  ROUNDS_PROGRESSION,
  COLORS,
  PAUSE_TEXTS,
  QUIT_TEXTS,
} from "../utils/constants.js";
import IntegritySystem from "../systems/IntegritySystem.js";
import VoteSystem from "../systems/VoteSystem.js";
import TimerSystem from "../systems/TimerSystem.js";
import DecaySystem from "../systems/DecaySystem.js";
import DamageSystem from "../systems/DamageSystem.js";
import UIManager from "../managers/UIManager.js";
import AudioManager from "../managers/AudioManager.js";
import House from "../components/House.js";
import { randomPosition, shake } from "../utils/helpers.js";
import {
  getHouseAssets,
  getRandomCrack,
  getRandomVine,
} from "../utils/constants.js";

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENES.GAME });
  }

  /**
   * Referencias a las imágenes de la casa
   */
  casaBuenaImage = null;
  casaDeterioradaImage = null;

  init(data) {
    // Recibir datos de ronda (si viene de Victoria)
    this.currentRound = data.round || 1;
    this.inheritedIntegrity = data.inheritedIntegrity || 100;

    // Validar que currentRound no exceda las rondas disponibles
    if (this.currentRound > ROUNDS_PROGRESSION.length) {
      console.warn(
        `⚠️ Ronda ${this.currentRound} no existe, reiniciando a Ronda 1`,
      );
      this.currentRound = 1;
      this.inheritedIntegrity = 100;
    }

    // Obtener configuración de la ronda
    this.roundConfig =
      ROUNDS_PROGRESSION[this.currentRound - 1] || ROUNDS_PROGRESSION[0];

    console.log(
      `🎮 Iniciando Ronda ${this.currentRound}: ${this.roundConfig.cycle}`,
    );
    console.log(
      `⚙️ Configuración: ${this.roundConfig.votes} votos, ${this.roundConfig.time}s, Integridad heredada: ${this.inheritedIntegrity}%`,
    );
  }

  create() {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Bandera para evitar múltiples llamadas a game over
    this.isGameEnding = false;

    // ========================================
    // 🎵 INICIALIZAR AUDIO MANAGER
    // ========================================
    this.audioManager = new AudioManager(this);
    this.audioManager.init();

    // Reproducir música del juego (game_theme.mp3 en loop)
    this.audioManager.playGameMusic();
    console.log("🎵 Música de juego iniciada");

    // ========================================
    // BACKGROUND DE LA RONDA
    // ========================================
    const bgKey = `bg_round_${this.currentRound}`;
    const background = this.add.image(width / 2, height / 2, bgKey);

    // Escalar para cubrir toda la pantalla
    const scaleX = width / background.width;
    const scaleY = height / background.height;
    const scale = Math.max(scaleX, scaleY);
    background.setScale(scale);
    background.setDepth(-1); // Detrás de todo

    console.log(`🎨 Background cargado: ${bgKey}`);

    this.renderHouseImages(width, height);

    // Inicializar sistemas
    this.initializeSystems();

    // Crear la casa
    this.house = new House(this);
    this.houseComponents = this.createHouseVisual();

    // Crear UI/HUD
    this.uiManager = new UIManager(this);
    this.uiManager.createHUD();
    this.uiManager.updateCycle(this.roundConfig.cycle);

    // Banderas de estado para pausa y quit
    this.isPaused = false;
    this.pauseOverlay = null;
    this.quitConfirmation = null;

    // Generar daños iniciales
    this.spawnInitialDamages();

    // Iniciar el juego
    this.startRound();

    // Setup de eventos
    this.setupEventListeners();

    console.log("✅ GameScene iniciado");
  }

  /**
   * Renderizar las dos capas de la casa según la ronda actual
   * Cada ronda tiene su propio estilo arquitectónico
   */
  renderHouseImages(width, height) {
    const centerX = width / 2;
    const centerY = height / 2;

    // Obtener casas de la ronda actual
    const houseAssets = getHouseAssets(this.currentRound);

    console.log(
      `🏠 Cargando casas de Ronda ${this.currentRound}: ${houseAssets.era}`,
    );

    // 🏠 CAPA 1: Casa bonita (base)
    this.casaBuenaImage = this.add.image(centerX, centerY, houseAssets.good);
    this.casaBuenaImage.setDisplaySize(width * 0.7, height * 0.7);
    this.casaBuenaImage.setDepth(0); // Fondo

    // 🏚️ CAPA 2: Casa deteriorada (encima)
    this.casaDeterioradaImage = this.add.image(
      centerX,
      centerY,
      houseAssets.bad,
    );
    this.casaDeterioradaImage.setDisplaySize(width * 0.7, height * 0.7);
    this.casaDeterioradaImage.setDepth(1); // Encima de la buena
    this.casaDeterioradaImage.setAlpha(1.0); // Completamente visible al inicio

    console.log(
      `✅ Casa renderizada: ${houseAssets.good} (base) + ${houseAssets.bad} (overlay)`,
    );
  }

  /**
   * Inicializar todos los sistemas
   */
  initializeSystems() {
    // Sistema de integridad
    this.integritySystem = new IntegritySystem(this);
    this.integritySystem.set(this.inheritedIntegrity);

    // Sistema de votos
    this.voteSystem = new VoteSystem(this, this.roundConfig.votes);

    // Sistema de timer
    this.timerSystem = new TimerSystem(this, this.roundConfig.time);

    // Sistema de decay
    this.decaySystem = new DecaySystem(this, this.roundConfig.decayInterval);

    // Sistema de daños
    this.damageSystem = new DamageSystem(this);

    console.log("🔧 Sistemas inicializados");
  }

  /**
   * Crear zonas interactivas invisibles sobre la casa
   * Ahora con subdivisiones para componentes multi
   */
  createHouseVisual() {
    const width = this.cameras.main.width;
    const centerX = width / 2;
    const startY = 150;

    const components = {};

    // Estructura visual de la casa
    const layout = [
      { id: "techo", y: startY, label: "TSE", width: 300, height: 60 },
      {
        id: "garantias",
        y: startY + 80,
        label: "Garantías",
        width: 300,
        height: 60,
      },
      {
        id: "paredes",
        y: startY + 160,
        label: "Corte",
        width: 300,
        height: 60,
      },
      {
        id: "municipalidades",
        y: startY + 240,
        label: "Municipalidades",
        width: 300,
        height: 60,
      },
      {
        id: "fundacion",
        y: startY + 320,
        label: "Constitución",
        width: 300,
        height: 60,
      },
    ];

    layout.forEach((item) => {
      const component = this.house.getComponent(item.id);
      const color = parseInt(component.color.replace("#", ""), 16);

      // Contenedor del componente
      const container = this.add.container(centerX, item.y);

      // ========================================
      // COMPONENTE SIMPLE: 1 zona completa
      // ========================================
      if (component.type === "simple") {
        const hitArea = this.add.rectangle(
          0,
          0,
          item.width,
          item.height,
          color,
          0,
        );
        hitArea.setInteractive({ useHandCursor: true });
        hitArea.on("pointerdown", () => this.onComponentClicked(item.id));

        // Hover debug
        hitArea.on("pointerover", () => {
          //hitArea.setStrokeStyle(2, 0xffffff, 0.5);
        });
        hitArea.on("pointerout", () => {
          hitArea.setStrokeStyle(0);
        });

        container.add([hitArea]);

        components[item.id] = {
          container: container,
          hitArea: hitArea,
          damages: [],
        };
      }

      // ========================================
      // COMPONENTE MULTI: Varias zonas
      // ========================================
      else if (component.type === "multi") {
        const subAreaCount = component.subAreas.length;
        const subWidth = item.width / subAreaCount;
        const startX = -(item.width / 2) + subWidth / 2;

        component.subAreas.forEach((subArea, index) => {
          const subX = startX + index * subWidth;

          const subHitArea = this.add.rectangle(
            subX,
            0,
            subWidth - 4, // Espacio de 4px entre sub-áreas
            item.height,
            color,
            0,
          );

          subHitArea.setInteractive({ useHandCursor: true });
          subHitArea.on("pointerdown", () => this.onComponentClicked(item.id));

          // Hover debug
          subHitArea.on("pointerover", () => {
            //subHitArea.setStrokeStyle(2, 0x00ff00, 0.5);
          });
          subHitArea.on("pointerout", () => {
            subHitArea.setStrokeStyle(0);
          });

          container.add(subHitArea);
        });

        components[item.id] = {
          container: container,
          damages: [],
        };
      }
    });

    console.log("🏠 Zonas interactivas creadas (con subdivisiones)");
    return components;
  }

  /**
   * Generar daños iniciales según configuración de ronda
   */
  spawnInitialDamages() {
    const { cracks, vines } = this.roundConfig;

    // Generar grietas
    for (let i = 0; i < cracks; i++) {
      this.spawnRandomDamage("crack");
    }

    // Generar vines
    for (let i = 0; i < vines; i++) {
      this.spawnRandomDamage("vine");
    }

    console.log(`💥 Daños iniciales: ${cracks} grietas, ${vines} vines`);
  }

  /**
   * Generar un daño aleatorio
   */
  spawnRandomDamage(damageType) {
    // Obtener todos los componentes de la casa
    const allComponents = this.house.getAllComponents();

    if (!allComponents || allComponents.length === 0) {
      console.warn("No hay componentes disponibles");
      return;
    }

    // Seleccionar componente aleatorio
    const component =
      allComponents[Phaser.Math.Between(0, allComponents.length - 1)];

    if (!component) {
      console.warn("Componente seleccionado es null");
      return;
    }

    // Verificar si puede recibir más daños
    if (this.damageSystem.componentAtMaxDamage(component)) {
      console.log(`${component.name} ya está al máximo de daños`);
      return; // Ya está al máximo
    }

    // Crear daño
    const damageTypeData =
      damageType === "crack"
        ? { id: "crack", name: "Grieta", symbol: "❌" }
        : { id: "vine", name: "Enredadera", symbol: "🌿" };

    const position = { x: 0, y: 0 }; // Relativo al componente

    const damageId = this.damageSystem.createDamage(
      component,
      damageTypeData,
      position,
    );

    // Agregar daño al componente
    component.addDamage({ id: damageId, type: damageTypeData });

    // Crear visual del daño
    this.createDamageVisual(component.id, damageId, damageTypeData);

    // Reducir integridad según tipo de componente
    let integrityLoss = 0;
    if (component.type === "simple") {
      integrityLoss = component.integrityValue / 2;
    } else if (component.type === "multi") {
      // Para multi, usar promedio de sub-áreas
      const avg =
        component.subAreas.reduce((sum, sa) => sum + sa.integrityValue, 0) /
        component.subAreas.length;
      integrityLoss = avg / 2;
    }

    if (integrityLoss > 0) {
      this.integritySystem.subtract(integrityLoss);
      // Actualizar apariencia de la casa
      this.updateHouseAppearance();
    }
  }

  /**
   * Crear visual de un daño usando assets reales
   */
  createDamageVisual(componentId, damageId, damageType) {
    const componentVisual = this.houseComponents[componentId];
    if (!componentVisual) return;

    // Seleccionar asset aleatorio según tipo de daño
    let assetKey;
    if (damageType.id === "crack") {
      assetKey = getRandomCrack();
    } else if (damageType.id === "vine") {
      assetKey = getRandomVine();
    } else {
      // Fallback (no debería pasar)
      console.warn("Tipo de daño desconocido:", damageType.id);
      return;
    }

    // Crear imagen del daño
    const damageImage = this.add.image(
      Phaser.Math.Between(-80, 80), // Posición X aleatoria
      Phaser.Math.Between(-20, 20), // Posición Y aleatoria
      assetKey,
    );
    damageImage.setOrigin(0.5);
    damageImage.setScale(0.8); // Ajustar tamaño (puedes cambiar esto)

    // Agregar al contenedor
    componentVisual.container.add(damageImage);

    // Guardar referencia
    componentVisual.damages.push({
      id: damageId,
      visual: damageImage,
    });

    // Animación de aparición
    damageImage.setScale(0);
    this.tweens.add({
      targets: damageImage,
      scale: 0.8, // Escala final
      duration: 300,
      ease: "Back.easeOut",
    });

    console.log(`💥 Daño visual creado: ${assetKey} en ${componentId}`);
  }

  /**
   * Cuando se hace clic en un componente
   */
  onComponentClicked(componentId) {
    // Verificar si hay votos
    if (!this.voteSystem.hasVotesLeft()) {
      console.log("❌ No hay votos disponibles");
      return;
    }

    const component = this.house.getComponent(componentId);

    // Verificar si tiene daños
    if (!component.hasDamage()) {
      console.log("ℹ️ Este componente no tiene daños");
      return;
    }

    // ========================================
    // 🎵 DETECTAR TIPO DE DAÑO PARA SONIDO
    // ========================================
    const damages = component.damages || [];
    const hasCrack = damages.some((d) => d.type.id === "crack");
    const hasVine = damages.some((d) => d.type.id === "vine");

    // Usar un voto
    this.voteSystem.useVote();

    // Reparar componente
    const integrityGained = component.repair();

    // Agregar integridad
    this.integritySystem.add(integrityGained);

    // Actualizar apariencia de la casa
    this.updateHouseAppearance();

    // Limpiar daños del damageSystem
    const damagesFromSystem =
      this.damageSystem.getDamagesByComponent(componentId);
    damagesFromSystem.forEach((damage) => {
      this.damageSystem.repairDamage(damage.id);
    });

    // Limpiar visuales de daños
    this.clearDamageVisuals(componentId);

    // ========================================
    // 🎵 REPRODUCIR SONIDO SEGÚN TIPO DE DAÑO
    // ========================================
    if (hasCrack) {
      this.audioManager.playClean(); // Sonido de limpiar grieta
    } else if (hasVine) {
      this.audioManager.playCut(); // Sonido de cortar enredadera
    }

    // Feedback visual y sonoro
    this.showRepairFeedback(componentId, integrityGained);

    console.log(`✅ ${component.name} reparado (+${integrityGained}%)`);
  }

  /**
   * Limpiar visuales de daños de un componente
   */
  clearDamageVisuals(componentId) {
    const componentVisual = this.houseComponents[componentId];
    if (!componentVisual) return;

    componentVisual.damages.forEach((damage) => {
      if (damage.visual) {
        // Animación de desaparición
        this.tweens.add({
          targets: damage.visual,
          scale: 0,
          alpha: 0,
          duration: 200,
          onComplete: () => {
            damage.visual.destroy();
          },
        });
      }
    });

    componentVisual.damages = [];
  }

  /**
   * Mostrar feedback de reparación
   */
  showRepairFeedback(componentId, integrityGained) {
    const componentVisual = this.houseComponents[componentId];

    // Efecto de pulso en hitArea (si existe)
    if (componentVisual && componentVisual.hitArea) {
      this.tweens.add({
        targets: componentVisual.hitArea,
        scale: 1.1,
        duration: 150,
        yoyo: true,
      });
    }

    // Solo mostrar mensaje si ganó integridad
    if (integrityGained > 0) {
      const x = componentVisual.container.x;
      const y = componentVisual.container.y;

      this.uiManager.showFloatingMessage(
        `+${integrityGained}% Integridad`,
        x,
        y - 40,
        COLORS.SUCCESS,
      );
    }

    // TODO: Reproducir sonido de reparación
    // this.audioManager.playRepair();
  }

  /**
   * Actualizar la opacidad de la casa deteriorada según integridad
   * A mayor integridad, más se ve la casa bonita (menos opacidad en deteriorada)
   */
  updateHouseAppearance() {
    if (!this.casaDeterioradaImage) return;

    const integrity = this.integritySystem.getCurrent();

    // Fórmula: a menor integridad, más visible la casa deteriorada
    // 100% integridad = alpha 0 (casa bonita visible)
    // 0% integridad = alpha 1 (casa deteriorada completamente visible)
    const alpha = 1 - integrity / 100;

    this.casaDeterioradaImage.setAlpha(alpha);

    console.log(
      `🎨 Casa actualizada: integridad=${integrity.toFixed(
        1,
      )}%, alpha=${alpha.toFixed(2)}`,
    );
  }

  /**
   * Iniciar la ronda
   */
  startRound() {
    // Iniciar timer
    this.timerSystem.start();

    // Iniciar decay
    this.decaySystem.start();

    console.log("▶️ Ronda iniciada");
  }

  /**
   * Setup de event listeners
   */
  setupEventListeners() {
    // Eventos del timer
    this.events.on("timerTick", (timeLeft) => {
      this.uiManager.updateTimer(timeLeft);
    });

    this.events.on("timeEnd", () => {
      this.onTimeEnd();
    });

    // Eventos de votos
    this.events.on("voteUsed", (data) => {
      this.uiManager.updateVotes(data.current, this.voteSystem.getInitial());
    });

    // Eventos de integridad
    this.events.on("integrityChanged", (integrity) => {
      this.uiManager.updateIntegrity(integrity);
    });

    // Eventos de decay
    this.events.on("decayDamageSpawned", (data) => {
      this.spawnRandomDamage(data.damageType.id);
    });
  }

  /**
   * Cuando se acaba el tiempo
   */
  onTimeEnd() {
    console.log("⏰ Tiempo agotado");
    this.checkWinLoseConditions();
  }

  /**
   * Verificar condiciones de victoria/derrota
   */
  checkWinLoseConditions() {
    // Detener sistemas
    this.timerSystem.stop();
    this.decaySystem.stop();

    const finalIntegrity = this.integritySystem.getCurrent();
    const votesUsed = this.voteSystem.getUsed();
    const totalVotes = this.voteSystem.getInitial();
    const votesLeft = this.voteSystem.getVotes();

    // Verificar victoria
    if (this.integritySystem.isAboveThreshold()) {
      this.onVictory(finalIntegrity, votesUsed, totalVotes);
    } else {
      this.onDefeat(finalIntegrity, votesUsed, totalVotes, votesLeft);
    }
  }

  /**
   * Victoria
   */
  onVictory(integrity, votesUsed, totalVotes) {
    if (this.timerSystem) this.timerSystem.destroy();
    if (this.decaySystem) this.decaySystem.destroy();
    if (this.uiManager) this.uiManager.destroy();

    console.log("🎉 ¡VICTORIA!");

    // ========================================
    // 🎵 REPRODUCIR SONIDO DE VICTORIA
    // ========================================
    this.audioManager.playWin();

    // Detener música del juego
    this.audioManager.stopMusic();

    // Transición a VictoryScene
    this.cameras.main.fade(500, 72, 187, 120);

    this.time.delayedCall(500, () => {
      this.scene.start(SCENES.VICTORY, {
        integrity: integrity,
        votesUsed: votesUsed,
        totalVotes: totalVotes,
        round: this.currentRound,
        nextCycle: this.getNextCycle(),
      });
    });
  }

  /**
   * Derrota
   */
  onDefeat(integrity, votesUsed, totalVotes, votesLeft) {
    if (this.isGameEnding && this.scene.isActive(SCENES.DEFEAT)) {
      return;
    }

    // Prevenir múltiples llamadas
    this.isGameEnding = true;

    console.log("💀 DERROTA");

    // ========================================
    // 🎵 REPRODUCIR SONIDO DE DERROTA
    // ========================================
    this.audioManager.playLose();

    // Detener música del juego
    this.audioManager.stopMusic();

    // Detener sistemas (NO destruir todavía, los necesitamos para la secuencia)
    if (this.timerSystem) this.timerSystem.stop();
    if (this.decaySystem) this.decaySystem.stop();

    // SECUENCIA DE DERROTA DRAMÁTICA
    this.showDefeatSequence(integrity, votesUsed, totalVotes, votesLeft);
  }

  /**
   * Secuencia dramática al perder
   */
  showDefeatSequence(integrity, votesUsed, totalVotes, votesLeft) {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // 1. MOSTRAR CASA DETERIORADA
    const houseAssets = getHouseAssets(this.currentRound);

    const casaDeteriorada = this.add.image(
      width / 2,
      height / 2,
      houseAssets.bad,
    );
    casaDeteriorada.setDisplaySize(width * 0.7, height * 0.7);
    casaDeteriorada.setDepth(999);
    casaDeteriorada.setAlpha(0);

    // Fade in de la casa deteriorada
    this.tweens.add({
      targets: casaDeteriorada,
      alpha: 1,
      duration: 1000,
      ease: "Cubic.easeIn",
    });

    // 2. TEMBLOR DE PANTALLA (2 segundos)
    this.cameras.main.shake(2000, 0.01);

    // 3. OVERLAY OSCURO GRADUAL
    const overlay = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      0x000000,
      0,
    );
    overlay.setDepth(1000);

    this.tweens.add({
      targets: overlay,
      alpha: 0.7,
      duration: 2000,
      ease: "Cubic.easeIn",
    });

    // 4. TEXTO "DEMOCRACIA PERDIDA" (aparece después de 1 segundo)
    this.time.delayedCall(1000, () => {
      const defeatText = this.add.text(
        width / 2,
        height / 2,
        "DEMOCRACIA PERDIDA",
        {
          fontSize: "48px",
          fontWeight: "bold",
          color: "#dc2626",
          fontFamily: "Arial",
          stroke: "#000000",
          strokeThickness: 6,
          align: "center",
        },
      );
      defeatText.setOrigin(0.5);
      defeatText.setAlpha(0);
      defeatText.setDepth(1001);

      // Animación de aparición
      this.tweens.add({
        targets: defeatText,
        alpha: 1,
        scale: { from: 0.5, to: 1.2 },
        duration: 800,
        ease: "Back.easeOut",
      });

      // Pulso
      this.tweens.add({
        targets: defeatText,
        scale: 1.1,
        duration: 500,
        yoyo: true,
        repeat: 2,
        delay: 800,
      });
    });

    // 5. LIMPIAR SISTEMAS Y TRANSICIÓN (después de 3.5 segundos)
    this.time.delayedCall(3500, () => {
      // Limpiar sistemas
      if (this.timerSystem) this.timerSystem.destroy();
      if (this.decaySystem) this.decaySystem.destroy();
      if (this.uiManager) this.uiManager.destroy();

      // Fade de cámara
      this.cameras.main.fade(500, 0, 0, 0);

      // Transición a DefeatScene
      this.time.delayedCall(500, () => {
        const reason = votesLeft > 5 ? "votes_left" : "low_integrity";

        this.scene.start(SCENES.DEFEAT, {
          integrity: integrity,
          votesUsed: votesUsed,
          totalVotes: totalVotes,
          votesLeft: votesLeft,
          cracksLeft: this.damageSystem ? this.damageSystem.getCrackCount() : 0,
          round: this.currentRound,
          reason: reason,
        });
      });
    });
  }

  /**
   * Obtener ciclo siguiente
   */
  getNextCycle() {
    const nextRound = this.currentRound + 1;
    const nextConfig = ROUNDS_PROGRESSION[nextRound - 1];
    return nextConfig ? nextConfig.cycle : "2042-2046";
  }

  update() {
    // Verificar si se acabaron los votos
    if (
      this.voteSystem &&
      this.voteSystem.allVotesUsed() &&
      this.timerSystem &&
      this.timerSystem.isRunning
    ) {
      console.log("🗳️ Votos agotados");
      // NO forzar game over, solo detener el timer
      this.timerSystem.stop();
      this.decaySystem.stop();

      // Esperar 1 segundo antes de verificar
      this.time.delayedCall(1000, () => {
        this.checkWinLoseConditions();
      });
    }

    // ... resto del código (componente crítico, etc)

    // Verificar si componente crítico falló (SOLO UNA VEZ)
    if (this.house && this.house.hasCriticalFailure() && !this.isGameEnding) {
      this.isGameEnding = true; // Bandera para evitar loop
      console.log("💥 Componente crítico colapsó");

      if (this.timerSystem) this.timerSystem.stop();
      if (this.decaySystem) this.decaySystem.stop();

      this.time.delayedCall(1000, () => {
        if (this.voteSystem && this.integritySystem) {
          this.onDefeat(
            this.integritySystem.getCurrent(),
            this.voteSystem.getUsed(),
            this.voteSystem.getInitial(),
            this.voteSystem.getVotes(),
          );
        }
      });
    }
  }

  /**
   * Pausar el juego
   */
  pauseGame() {
    if (this.isPaused) return;

    this.isPaused = true;

    // Pausar sistemas
    if (this.timerSystem) this.timerSystem.pause();
    if (this.decaySystem) this.decaySystem.pause();

    // Pausar física y tweens
    this.physics.pause();
    this.tweens.pauseAll();

    // Deshabilitar interacciones con componentes
    this.disableComponentInteractions();

    // Mostrar overlay de pausa
    this.showPauseOverlay();

    console.log("⏸ Juego pausado");
  }

  /**
   * Reanudar el juego
   */
  resumeGame() {
    if (!this.isPaused) return;

    this.isPaused = false;

    // Reanudar sistemas
    if (this.timerSystem) this.timerSystem.resume();
    if (this.decaySystem) this.decaySystem.resume();

    // Reanudar física y tweens
    this.physics.resume();
    this.tweens.resumeAll();

    // Reactivar interacciones con componentes
    this.enableComponentInteractions();

    // Ocultar overlay
    this.hidePauseOverlay();

    console.log("▶️ Juego reanudado");
  }

  /**
   * Mostrar overlay de pausa
   */
  showPauseOverlay() {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Overlay oscuro
    const overlay = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      0x000000,
      0.8,
    );
    overlay.setDepth(9000);

    // Título
    const title = this.add.text(width / 2, height * 0.3, PAUSE_TEXTS.TITLE, {
      fontSize: "48px",
      fontWeight: "bold",
      color: "#ffffff",
      fontFamily: "Arial",
    });
    title.setOrigin(0.5);
    title.setDepth(9001);

    // Botón CONTINUAR
    const continueBtn = this.createPauseButton(
      width / 2,
      height * 0.5,
      PAUSE_TEXTS.CONTINUE,
      0x48bb78,
      () => this.resumeGame(),
    );

    // Botón MUTE/UNMUTE
    const muteText = this.audioManager?.getMuteState()
      ? "🔊 Activar Sonido"
      : "🔇 Silenciar";

    const muteBtn = this.createPauseButton(
      width / 2,
      height * 0.62,
      muteText,
      0x4a5568,
      () => {
        this.audioManager?.toggleMute();
        this.uiManager?.updateMuteIcon(this.audioManager.getMuteState());

        // Actualizar texto del botón
        const newText = this.audioManager.getMuteState()
          ? "🔊 Activar Sonido"
          : "🔇 Silenciar";
        muteBtn.text.setText(newText);
      },
    );

    // Botón SALIR
    const quitBtn = this.createPauseButton(
      width / 2,
      height * 0.74,
      PAUSE_TEXTS.QUIT,
      0xdc2626,
      () => {
        this.hidePauseOverlay();
        this.showQuitConfirmation();
      },
    );

    // Guardar referencias
    this.pauseOverlay = {
      overlay,
      title,
      continueBtn,
      muteBtn,
      quitBtn,
    };
  }

  /**
   * Ocultar overlay de pausa
   */
  /**
   * Ocultar overlay de pausa
   */
  hidePauseOverlay() {
    if (!this.pauseOverlay) return;

    // Destruir overlay oscuro
    if (this.pauseOverlay.overlay) {
      this.pauseOverlay.overlay.destroy();
    }

    // Destruir título
    if (this.pauseOverlay.title) {
      this.pauseOverlay.title.destroy();
    }

    // Destruir botones
    if (this.pauseOverlay.continueBtn) {
      if (this.pauseOverlay.continueBtn.bg)
        this.pauseOverlay.continueBtn.bg.destroy();
      if (this.pauseOverlay.continueBtn.text)
        this.pauseOverlay.continueBtn.text.destroy();
    }

    if (this.pauseOverlay.muteBtn) {
      if (this.pauseOverlay.muteBtn.bg) this.pauseOverlay.muteBtn.bg.destroy();
      if (this.pauseOverlay.muteBtn.text)
        this.pauseOverlay.muteBtn.text.destroy();
    }

    if (this.pauseOverlay.quitBtn) {
      if (this.pauseOverlay.quitBtn.bg) this.pauseOverlay.quitBtn.bg.destroy();
      if (this.pauseOverlay.quitBtn.text)
        this.pauseOverlay.quitBtn.text.destroy();
    }

    this.pauseOverlay = null;

    console.log("✅ Overlay de pausa ocultado");
  }

  /**
   * Crear botón para overlay de pausa
   */
  createPauseButton(x, y, text, color, callback) {
    const bg = this.add.rectangle(x, y, 300, 60, color);
    bg.setStrokeStyle(4, 0xffffff);
    bg.setInteractive({ useHandCursor: true });
    bg.setDepth(9001);

    const buttonText = this.add.text(x, y, text, {
      fontSize: "24px",
      fontWeight: "bold",
      color: "#ffffff",
      fontFamily: "Arial",
    });
    buttonText.setOrigin(0.5);
    buttonText.setDepth(9002);

    // Hover
    bg.on("pointerover", () => {
      bg.setScale(1.05);
      buttonText.setScale(1.05);
    });

    bg.on("pointerout", () => {
      bg.setScale(1.0);
      buttonText.setScale(1.0);
    });

    bg.on("pointerdown", callback);

    return { bg, text: buttonText };
  }

  /**
   * Mostrar confirmación de salir
   */
  showQuitConfirmation() {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Pausar el juego SIN mostrar overlay de pausa
    if (!this.isPaused) {
      this.isPaused = true;

      // Pausar sistemas
      if (this.timerSystem) this.timerSystem.pause();
      if (this.decaySystem) this.decaySystem.pause();

      // Pausar física y tweens
      this.physics.pause();
      this.tweens.pauseAll();

      // Deshabilitar interacciones
      this.disableComponentInteractions();

      console.log("⏸ Juego pausado (quit directo)");
    }

    // Overlay oscuro
    const overlay = this.add.rectangle(
      width / 2,
      height / 2,
      width,
      height,
      0x000000,
      0.9,
    );
    overlay.setDepth(9500);

    // Título
    const title = this.add.text(width / 2, height * 0.35, QUIT_TEXTS.TITLE, {
      fontSize: "36px",
      fontWeight: "bold",
      color: "#ffffff",
      fontFamily: "Arial",
    });
    title.setOrigin(0.5);
    title.setDepth(9501);

    // Mensaje
    const message = this.add.text(
      width / 2,
      height * 0.45,
      QUIT_TEXTS.MESSAGE,
      {
        fontSize: "18px",
        color: "#cccccc",
        fontFamily: "Arial",
        align: "center",
      },
    );
    message.setOrigin(0.5);
    message.setDepth(9501);

    // Botón CONFIRMAR
    const confirmBtn = this.createQuitButton(
      width / 2 - 100,
      height * 0.6,
      QUIT_TEXTS.CONFIRM,
      0xdc2626,
      () => this.confirmQuit(),
    );

    // Botón CANCELAR
    const cancelBtn = this.createQuitButton(
      width / 2 + 100,
      height * 0.6,
      QUIT_TEXTS.CANCEL,
      0x4a5568,
      () => this.cancelQuit(),
    );

    // Guardar referencias
    this.quitConfirmation = {
      overlay,
      title,
      message,
      confirmBtn,
      cancelBtn,
    };
  }

  /**
   * Ocultar confirmación de salir
   */
  hideQuitConfirmation() {
    if (!this.quitConfirmation) return;

    // Destruir overlay
    if (this.quitConfirmation.overlay) {
      this.quitConfirmation.overlay.destroy();
    }

    // Destruir título
    if (this.quitConfirmation.title) {
      this.quitConfirmation.title.destroy();
    }

    // Destruir mensaje
    if (this.quitConfirmation.message) {
      this.quitConfirmation.message.destroy();
    }

    // Destruir botones
    if (this.quitConfirmation.confirmBtn) {
      if (this.quitConfirmation.confirmBtn.bg)
        this.quitConfirmation.confirmBtn.bg.destroy();
      if (this.quitConfirmation.confirmBtn.text)
        this.quitConfirmation.confirmBtn.text.destroy();
    }

    if (this.quitConfirmation.cancelBtn) {
      if (this.quitConfirmation.cancelBtn.bg)
        this.quitConfirmation.cancelBtn.bg.destroy();
      if (this.quitConfirmation.cancelBtn.text)
        this.quitConfirmation.cancelBtn.text.destroy();
    }

    this.quitConfirmation = null;

    console.log("✅ Confirmación de quit ocultada");
  }

  /**
   * Crear botón para confirmación de quit
   */
  createQuitButton(x, y, text, color, callback) {
    const bg = this.add.rectangle(x, y, 180, 60, color);
    bg.setStrokeStyle(4, 0xffffff);
    bg.setInteractive({ useHandCursor: true });
    bg.setDepth(9501);

    const buttonText = this.add.text(x, y, text, {
      fontSize: "20px",
      fontWeight: "bold",
      color: "#ffffff",
      fontFamily: "Arial",
    });
    buttonText.setOrigin(0.5);
    buttonText.setDepth(9502);

    // Hover
    bg.on("pointerover", () => {
      bg.setScale(1.05);
      buttonText.setScale(1.05);
    });

    bg.on("pointerout", () => {
      bg.setScale(1.0);
      buttonText.setScale(1.0);
    });

    bg.on("pointerdown", callback);

    return { bg, text: buttonText };
  }

  /**
   * Confirmar salir al menú
   */
  confirmQuit() {
    console.log("🚪 Saliendo al menú...");

    // Ocultar overlays primero
    this.hideQuitConfirmation();
    this.hidePauseOverlay();

    // Detener TODOS los sistemas antes de destruir
    if (this.timerSystem) {
      this.timerSystem.stop();
      this.timerSystem.destroy();
    }

    if (this.decaySystem) {
      this.decaySystem.stop();
      this.decaySystem.destroy();
    }

    // Detener música
    if (this.audioManager) {
      this.audioManager.stopMusic();
    }

    // Limpiar UI
    if (this.uiManager) {
      this.uiManager.destroy();
    }

    // Fade y transición
    this.cameras.main.fade(500, 0, 0, 0);

    this.time.delayedCall(500, () => {
      // Limpiar audio al final
      if (this.audioManager) {
        this.audioManager.destroy();
      }

      this.scene.start(SCENES.MENU);
    });
  }

  /**
   * Cancelar salir (volver al juego o pausa)
   */
  cancelQuit() {
    this.hideQuitConfirmation();

    // Si se pausó desde el botón X (sin overlay de pausa visible)
    // entonces reanudar el juego directamente
    if (this.isPaused && !this.pauseOverlay) {
      this.resumeGame();
    }
    // Si hay overlay de pausa visible, volver a mostrarlo
    else if (this.isPaused && this.pauseOverlay) {
      this.showPauseOverlay();
    }
  }

  /**
   * Deshabilitar interacciones con componentes
   */
  disableComponentInteractions() {
    if (!this.houseComponents) return;

    Object.values(this.houseComponents).forEach((component) => {
      if (component.hitArea) {
        component.hitArea.disableInteractive();
      }
    });
  }

  /**
   * Reactivar interacciones con componentes
   */
  enableComponentInteractions() {
    if (!this.houseComponents) return;

    Object.values(this.houseComponents).forEach((component) => {
      if (component.hitArea) {
        component.hitArea.setInteractive({ useHandCursor: true });
      }
    });
  }
}
